import type { KeyboardEvent, ReactNode } from "react";
import { useCallback } from "react";
import type { ClientHubBrandingV1 } from "@enrein/shared-contracts/client-link";
import type { GreenfieldClientTabId } from "../types.js";
import { GREENFIELD_TAB_LABELS } from "../types.js";
import { brandingLetterMark } from "../adapters/pwa-branding.js";

export function GreenfieldClientNav({
  activeTab,
  onTabChange,
  tabIds,
}: {
  readonly activeTab: GreenfieldClientTabId;
  readonly onTabChange: (tab: GreenfieldClientTabId) => void;
  readonly tabIds: readonly GreenfieldClientTabId[];
}) {
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
      let next = index;
      if (event.key === "ArrowRight") next = (index + 1) % tabIds.length;
      if (event.key === "ArrowLeft") next = (index - 1 + tabIds.length) % tabIds.length;
      if (event.key === "Home") next = 0;
      if (event.key === "End") next = tabIds.length - 1;
      if (next !== index) {
        event.preventDefault();
        onTabChange(tabIds[next]!);
        const buttons = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
        buttons?.[next]?.focus();
      }
    },
    [onTabChange, tabIds],
  );

  return (
    <nav aria-label="Client workspace">
      <ul
        role="tablist"
        data-testid="greenfield-client-tabs"
        style={{ display: "flex", gap: 8, listStyle: "none", padding: 0, margin: 0, flexWrap: "wrap" }}
      >
        {tabIds.map((tabId, index) => {
          const selected = activeTab === tabId;
          return (
            <li key={tabId}>
              <button
                type="button"
                role="tab"
                id={`gf-tab-btn-${tabId}`}
                className={selected ? "ws-btn ws-btn-primary" : "ws-btn"}
                aria-selected={selected}
                aria-controls={`gf-tab-${tabId}`}
                aria-current={selected ? "page" : undefined}
                tabIndex={selected ? 0 : -1}
                onClick={() => onTabChange(tabId)}
                onKeyDown={(event) => onKeyDown(event, index)}
              >
                {GREENFIELD_TAB_LABELS[tabId]}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

export function GreenfieldClientShell({
  branding,
  children,
}: {
  readonly branding: ClientHubBrandingV1;
  readonly children: ReactNode;
}) {
  return (
    <div className="enrein-workspace-v1" data-greenfield-client-hub="true" data-testid="greenfield-client-hub">
      <a className="ws-skip-link" href="#client-hub-main">
        Skip to content
      </a>
      <header
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
          alignItems: "center",
          padding: "16px 24px",
          borderBottom: "1px solid var(--ws-border-subtle)",
          background: "var(--ws-surface-raised)",
        }}
      >
        <div
          data-testid="client-hub-brand"
          style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}
        >
          {branding.logoUrl ? (
            <img
              src={branding.logoUrl}
              alt=""
              width={32}
              height={32}
              data-testid="client-hub-logo"
              style={{ borderRadius: 8, objectFit: "cover" }}
            />
          ) : (
            <span
              aria-hidden="true"
              data-testid="client-hub-letter-mark"
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: branding.primaryColor ?? "var(--ws-primary-action)",
                color: "var(--ws-surface-raised)",
                fontWeight: 600,
              }}
            >
              {brandingLetterMark(branding.businessDisplayName)}
            </span>
          )}
          <strong data-testid="client-hub-business-name">{branding.businessDisplayName}</strong>
        </div>
        <p className="ws-muted" style={{ margin: 0 }} data-testid="client-hub-agency">
          Supported by {branding.agencyDisplayName}
        </p>
      </header>
      <main id="client-hub-main" style={{ padding: "24px", maxWidth: 1100, margin: "0 auto" }}>
        {children}
      </main>
    </div>
  );
}

export function GreenfieldClientBootShell({
  title,
  children,
  denial = false,
}: {
  readonly title: string;
  readonly children: ReactNode;
  readonly denial?: boolean;
}) {
  return (
    <div className="enrein-workspace-v1" data-greenfield-client-boot="true" data-testid="greenfield-client-boot">
      <a className="ws-skip-link" href="#client-hub-main">
        Skip to content
      </a>
      <main id="client-hub-main" style={{ padding: "24px", maxWidth: 480, margin: "48px auto" }}>
        <section
          className="ws-card"
          role={denial ? "alert" : undefined}
          tabIndex={denial ? 0 : undefined}
          data-testid={denial ? "client-link-error-panel" : undefined}
        >
          <h1 className="ws-display">{title}</h1>
          {children}
        </section>
      </main>
    </div>
  );
}
